import { useState } from 'react';
import { Button } from './Button';
import { apiSend, ClientApiError } from '../api/client';
import { formatDateTime } from '../lib/format';
import { inputStyle } from './Field';
import type { ReferralNote } from '../../shared/types';

export function NotesPanel({
  referralId,
  notes,
  canAdd,
  onAdded,
  onReload,
}: {
  referralId: string;
  notes: ReferralNote[];
  canAdd: boolean;
  onAdded: (note: ReferralNote) => void;
  onReload: () => void | Promise<void>;
}) {
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (body.trim() === '') return;
    setBusy(true);
    setError('');
    try {
      const { note } = await apiSend<{ note: ReferralNote }>(
        `/api/referrals/${referralId}/notes`,
        'POST',
        { body },
      );
      onAdded(note);
      setBody('');
      // Optimistic append above only patches the note list; the referral
      // itself may have changed underneath (another dispatcher completed or
      // declined it while this note was being typed), so the parent must
      // reload to keep its derived state (the Assign/Recon/Complete/Decline
      // controls) honest.
      await onReload();
    } catch (caught) {
      if (caught instanceof ClientApiError && caught.code === 'referral_changed') {
        // The referral went terminal underneath this insert. Keep the typed
        // text so the user does not lose it, but reload the parent so the
        // controls reflect the true current status instead of a stale one.
        setError(caught.message);
        await onReload();
      } else {
        setError(caught instanceof ClientApiError ? caught.message : 'Could not save that note.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>Notes</h2>
      {notes.length === 0 && <p className="muted">No notes yet.</p>}
      {notes.map((note) => (
        <div key={note.id} style={{ borderTop: '1px solid var(--line)', padding: '0.6rem 0' }}>
          <p className="muted" style={{ margin: 0 }}>
            {note.authorName} · {formatDateTime(note.createdAt)}
          </p>
          <p style={{ margin: '0.2rem 0 0', whiteSpace: 'pre-wrap' }}>{note.body}</p>
        </div>
      ))}

      {canAdd && (
        <form onSubmit={submit} style={{ marginTop: '0.75rem' }}>
          {error && <div className="error-banner">{error}</div>}
          <label htmlFor="note-body" style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem' }}>
            Add a note
          </label>
          <textarea
            id="note-body"
            rows={3}
            style={{ ...inputStyle, minHeight: '5rem', resize: 'vertical' }}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <Button type="submit" full disabled={busy || body.trim() === ''} style={{ marginTop: '0.5rem' }}>
            {busy ? 'Saving' : 'Save note'}
          </Button>
        </form>
      )}
    </div>
  );
}
